import {
  BarChart3,
  BookOpen,
  Calculator,
  ChevronDown,
  FileText,
  HelpCircle,
  Landmark,
  Receipt,
  Scale,
  Shield,
} from "lucide-react";
import { useState } from "react";

const REPORT_GUIDE_SECTIONS = [
  {
    id: "understanding",
    icon: Receipt,
    title: "Understanding TDS",
    content: [
      "TDS is tax deducted from salary each month and deposited with the government.",
      "Use this module to generate registers, projections, and statutory certificates.",
    ],
  },
  {
    id: "old-regime",
    icon: Landmark,
    title: "Old Tax Regime",
    content: [
      "Allows HRA, LTA, 80C, 80D and other Chapter VI-A deductions.",
      "Best when employees claim multiple exemptions.",
    ],
  },
  {
    id: "new-regime",
    icon: Shield,
    title: "New Tax Regime",
    content: [
      "Lower slab rates with limited deductions.",
      "Simpler declarations and faster payroll processing.",
    ],
  },
  {
    id: "monthly-register",
    icon: BarChart3,
    title: "Monthly Register",
    content: [
      "Shows taxable income, annual tax, and monthly TDS per employee.",
      "Export to CSV for audit and reconciliation.",
    ],
  },
  {
    id: "tax-computation",
    icon: Calculator,
    title: "Employee Tax Computation",
    content: [
      "Detailed projection of income, exemptions, and tax liability.",
      "Based on live employee CTC and approved declarations.",
    ],
  },
  {
    id: "quarterly",
    icon: FileText,
    title: "Quarterly Return",
    content: [
      "Form 24Q is filed every quarter with employee-wise TDS details.",
      "Track filing status and acknowledgement numbers here.",
    ],
  },
  {
    id: "form16",
    icon: FileText,
    title: "Form 16",
    content: [
      "Annual tax certificate for employees after year-end.",
      "Includes Part A (TDS deposited) and Part B (computation).",
    ],
  },
  {
    id: "investment",
    icon: Shield,
    title: "Investment Declaration",
    content: [
      "Employees declare 80C, 80D, HRA and other exemptions.",
      "Declarations drive projected tax in computation reports.",
    ],
  },
  {
    id: "proof",
    icon: FileText,
    title: "Proof Verification",
    content: [
      "HR verifies submitted proofs against declarations.",
      "Approved amounts reduce taxable income in reports.",
    ],
  },
  {
    id: "challan",
    icon: Landmark,
    title: "Challan Management",
    content: [
      "TDS must be deposited via challan before filing returns.",
      "Track deposits in the Compliance Center.",
    ],
  },
  {
    id: "reconciliation",
    icon: Scale,
    title: "TDS Reconciliation",
    content: [
      "Match payroll deductions with challan deposits.",
      "Use Compliance Center for full reconciliation workflow.",
    ],
  },
  {
    id: "projection",
    icon: BarChart3,
    title: "Tax Projection",
    content: [
      "Annual projection uses live CTC and approved exemptions.",
      "Helps forecast year-end tax and monthly TDS.",
    ],
  },
  {
    id: "faq",
    icon: HelpCircle,
    title: "Frequently Asked Questions",
    content: [
      "When should I generate Form 16? After the financial year ends and payroll is finalized.",
      "Can I export reports? Yes — Excel/CSV via the Export panel.",
    ],
  },
  {
    id: "compliance",
    icon: Shield,
    title: "Government Compliance Notes",
    content: [
      "Form 24Q must be filed quarterly with the Income Tax Department.",
      "Form 16 must be issued to employees by the statutory due date.",
    ],
  },
];

function Accordion({ sections, defaultOpen = "understanding" }) {
  const [openId, setOpenId] = useState(defaultOpen);
  return (
    <div className="tdsr-guide-accordion">
      {sections.map((sec) => {
        const Icon = sec.icon;
        const open = openId === sec.id;
        return (
          <div key={sec.id} className="tdsr-guide-item">
            <button
              type="button"
              className="tdsr-guide-item__head"
              onClick={() => setOpenId(open ? null : sec.id)}
              aria-expanded={open}
            >
              <span className="tdsr-guide-item__title">
                <Icon size={14} />
                {sec.title}
              </span>
              <ChevronDown size={14} className={open ? "tdsr-guide-item__chev--open" : ""} />
            </button>
            {open && (
              <div className="tdsr-guide-item__body">
                <ul>
                  {sec.content.map((line) => (
                    <li key={line}>{line}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default function TdsReportsGuidePanel({ className = "", onMobileClose }) {
  return (
    <aside className={`tdsr-guide ${className}`.trim()} aria-label="TDS Reports user guide">
      <div className="tdsr-guide__head">
        <BookOpen size={16} />
        <div>
          <h3 className="tdsr-guide__title">User Guide</h3>
          <p className="tdsr-guide__sub">Simple help for each report</p>
        </div>
        {onMobileClose && (
          <button type="button" className="tdsr-guide__close" onClick={onMobileClose} aria-label="Close guide">
            ×
          </button>
        )}
      </div>
      <Accordion sections={REPORT_GUIDE_SECTIONS} />
    </aside>
  );
}
